import { takeLatest, put, call, select, all } from 'redux-saga/effects';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
    deleteList,
    deleteAllItemsList,
    saveToList,
    getList,
    shareList,
} from 'yoda-interfaces/lib/MyList/MyList';
import FSaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { doPostSSRLogs } from 'yoda-core-components/lib/helpers/Utils/Utils';
import * as SavedListActionTypes from '../actionTypes/SavedListActionTypes';
import * as MyListActionTypes from '../actionTypes/MyListActionTypes';
import ApplicationLogger from '../helpers/ApplicationLogger';
import {
    getHighPriorityItems,
    updateMostWanted,
    removeSavedItemAtLocal,
    removeDPSavedItems,
} from '../common/getSavedItems';

const logger = ApplicationLogger.getLogger();

export function* deleteListSaga(action) {
    try {
        const deleteAction = {};
        deleteAction.type = action.type;
        deleteAction.payload = action.payload;
        const { ppId } = action.payload;
        removeDPSavedItems({ id: ppId });
        const payload = {
            deleteId: action.payload.listId,
        };
        payload.isLoading = false;
        payload.requestFrom = 'DELETE';
        let api = deleteList;
        if (action.payload.deleteAll) {
            api = deleteAllItemsList;
        }
        const list = yield call(FSaga, api, deleteAction, undefined, false);
        const statusCode = get(list, 'status') || get(list, 'response.status');
        if (list.isSuccess) {
            yield all([
                put({
                    type: SavedListActionTypes.STORE_LAST_REMOVED_ITEM,
                    payload: action.removedItem,
                }),
                put({
                    type: SavedListActionTypes.DELETE_SAVED_LIST_SUCCESS,
                    list,
                    itemId: action.payload.itemId,
                }),
                put({
                    type: SavedListActionTypes.DELETE_SAVED_LIST_SUCCESS_EVENT,
                    payload: action.payload,
                }),
                put({ type: MyListActionTypes.GET_LIST, payload }),
            ]);
        } else {
            const deleteListError = {
                isSuccess: false,
                error: {
                    errorCode: get(
                        list,
                        'response.data.errorCode',
                        'SRV_DOWN_STREAM_API_DOWN_ERROR'
                    ),
                    errorMessage: get(list, 'response.data.errorMessage', ''),
                    statusCode,
                },
            };
            yield put({ type: SavedListActionTypes.DELETE_SAVED_LIST_ERROR, deleteListError });
        }
    } catch (error) {
        yield put({
            type: SavedListActionTypes.DELETE_SAVED_LIST_ERROR,
            error,
        });
    }
}

export function* guestModeToggleMostWanted(action) {
    const { uuid, mostWanted } = action.payload;
    updateMostWanted(uuid, mostWanted);
    yield put({
        type: SavedListActionTypes.UPDATE_MOST_WANTED_IN_STORE,
        payload: { uuid, mostWanted },
    });
}

export function* guestModeDeleteItem(action) {
    const { id, itemId } = action.payload;
    removeSavedItemAtLocal({ id, itemId });
    const uuid = `${id}-${itemId || ''}`;
    yield put({ type: SavedListActionTypes.REMOVE_SAVED_LIST_ITEM, payload: { uuid } });
}

export function* toggleMostWantedSaga(action) {
    try {
        const savedListAction = {};
        const { listId, savedItemId, newValue } = action.payload;
        const params = [
            {
                op: 'replace',
                path: '/mostWanted',
                value: newValue.toString(),
            },
        ];
        savedListAction.type = action.type;
        savedListAction.isLoaded = true;
        savedListAction.payload = { listId, savedItemId, params };
        const response = yield call(FSaga, saveToList, savedListAction, undefined, false);

        if (response.isSuccess) {
            yield put({ type: SavedListActionTypes.TOGGLE_MOST_WANTED_IN_LIST_SUCCESS, response });
        } else {
            yield put({
                type: SavedListActionTypes.TOGGLE_MOST_WANTED_IN_LIST_ERROR,
                error: response.response.data,
            });
        }
    } catch (error) {
        yield put({
            type: SavedListActionTypes.TOGGLE_MOST_WANTED_IN_LIST_ERROR,
            error,
        });
    }
}

const getStoreData = (state) => state.savedList;

const addMostWantedForGuest = (items) => {
    if (isEmpty(items)) {
        return;
    }
    const highPriorityItems = getHighPriorityItems();
    items.forEach((item) => {
        const lsItem = highPriorityItems[item.id];
        // eslint-disable-next-line no-param-reassign
        item.mostWanted = lsItem.mostWanted;
        // eslint-disable-next-line no-param-reassign
        item.quantity = lsItem.quantity;
    });
};

export function* getSavedListSaga(action) {
    try {
        const mylistAction = {};
        mylistAction.type = action.type;
        mylistAction.payload = action.payload;
        mylistAction.headers = action.headers;
        if (action.payload.token) {
            mylistAction.payload.isAuthRequired = false;
        }

        if (action.payload.isTrendingList) {
            mylistAction.payload.isTrendingList = true;
        }

        const savedList = yield call(FSaga, getList, mylistAction, undefined, false);
        const { response } = savedList;
        const statusCode = get(response, 'status') || get(response, 'response.status');
        if (statusCode && statusCode === 200) {
            savedList.isGuestMode = action.payload.isGuestMode;
            savedList.isLoaded = true;
            doPostSSRLogs(logger, {
                requestInfo: `Yoda-Listing || List Detail || Saved Saga | getListSaga() || Status: ${response.status}`,
                response,
            });
            doPostSSRLogs(logger, {
                requestInfo: `Yoda-Listing || List Detail || Saved Saga | getListSaga() || Status Text:
                ${response.statusText}`,
                response,
            });
            doPostSSRLogs(logger, {
                requestInfo: `Yoda-Listing || List Detail || Saved Saga | getListSaga() || response: ${response}`,
                response,
            });
            if (savedList.isGuestMode && !get(savedList, 'response.data.shareToken')) {
                addMostWantedForGuest(get(savedList, 'response.data.items', []));
            }
            yield put({ type: SavedListActionTypes.GET_SAVED_LIST_SUCCESS, savedList });
        } else {
            yield put({
                type: SavedListActionTypes.GET_SAVED_LIST_ERROR,
                error: { ...response.data, statusCode },
            });
        }
    } catch (error) {
        yield put({
            type: SavedListActionTypes.GET_SAVED_LIST_ERROR,
            error,
        });
    }
}

export function* getListCategory(action) {
    try {
        const mylistAction = {};
        mylistAction.type = action.type;
        mylistAction.payload = action.payload;
        mylistAction.headers = action.headers;
        if (action.payload.token) {
            mylistAction.payload.isAuthRequired = false;
        }
        const { savedList } = yield select(getStoreData);
        const isGuestUser = action.payload.isGuestMode;
        let items = get(savedList, 'data.items', []);
        if (isGuestUser) {
            const data = get(action, 'payload.data', '');
            const persistItems = data.split(',').filter((it) => it);
            if (items.length !== persistItems.length) {
                items = [];
            }
        }
        const sort = get(action, 'payload.params.sort', 'priority');
        if (isEmpty(items) || !savedList.isGuestMode) {
            if (isGuestUser) {
                mylistAction.payload.params.sort = '';
            }
            const resultList = yield call(FSaga, getList, mylistAction, undefined, false); // uncomment when API is giving proper response
            const sortList = resultList.response;
            sortList.isGuestMode = isGuestUser;
            sortList.isLoaded = true;
            yield put({
                type: SavedListActionTypes.GET_LIST_CATEGORY_SUCCESS,
                payload: { sortList, sort },
            });
        } else {
            yield put({ type: SavedListActionTypes.UPDATE_SORT_SAVED_ITEMS, payload: { sort } });
        }
    } catch (error) {
        yield put({
            type: SavedListActionTypes.GET_LIST_CATEGORY_ERROR,
            error,
        });
    }
}

export function* shareListSaga(action) {
    try {
        const shareAction = {};
        shareAction.type = action.type;
        shareAction.payload = action.payload;
        const share = yield call(FSaga, shareList, shareAction, undefined, false);

        if (share.isSuccess) {
            const response = get(share, 'response.data', {});
            yield put({ type: SavedListActionTypes.SHARE_LIST_SUCCESS, response });
        } else {
            const error = get(share, 'response.data', { errorCode: '' });

            const errorResponse = { error };
            yield put({
                type: SavedListActionTypes.SHARE_LIST_ERROR,
                errorResponse,
            });
        }
    } catch (err) {
        const errorResponse = {
            error: {
                errorMessage: err.message,
                errorCode: 'EXCEPTION_IN_SHARE_LIST_SAGA',
            },
        };
        yield put({
            type: SavedListActionTypes.SHARE_LIST_ERROR,
            errorResponse,
        });
    }
}

const watchSavedListSaga = function* watchSavedListSaga() {
    yield takeLatest(SavedListActionTypes.DELETE_SAVED_LIST, deleteListSaga);
    yield takeLatest(SavedListActionTypes.GET_SAVED_LIST, getSavedListSaga);
    yield takeLatest(SavedListActionTypes.GET_LIST_CATEGORY, getListCategory);
    yield takeLatest(SavedListActionTypes.SHARE_LIST, shareListSaga);
    yield takeLatest(SavedListActionTypes.TOGGLE_MOST_WANTED_IN_LIST, toggleMostWantedSaga);
    yield takeLatest(SavedListActionTypes.UPDATE_MOST_WANTED, guestModeToggleMostWanted);
    yield takeLatest(SavedListActionTypes.DELETE_SAVED_LIST_ITEM, guestModeDeleteItem);
};
watchSavedListSaga.sagaName = 'watchSavedListSaga';

export default watchSavedListSaga;
